import { useState } from "react";
import { supabase } from "../lib/SupabaseClient";

interface ExamJson {
    Title: string;
    Description: string;
    DurationMinutes: number;
    Id: number;
    Questions: {
        QuestionText: string;
        OptionA: string;
        OptionB: string;
        OptionC: string;
        OptionD: string;
        HelperFileName: string | null;
    }[];
}

export default function ImportExamPage({
    organizationId,
    createdBy,
}: {
    organizationId: number;
    createdBy: string;
}) {
    const [exam, setExam] = useState<ExamJson | null>(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    // Parse uploaded JSON
    async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        const text = await file.text();
        try {
            const parsed: ExamJson = JSON.parse(text);
            setExam(parsed);
            setMessage("");
        } catch {
            setMessage("❌ Invalid JSON file");
        }
    }

    // Save exam to Supabase
    async function handleImport() {
        if (!exam) return;
        try {
            setLoading(true);
            setMessage("");

            // 1. Insert test
            const { data: test, error: testError } = await supabase
                .from("tests")
                .insert([
                    {
                        name: exam.Title,
                        description: exam.Description,
                        duration_minutes: exam.DurationMinutes,
                        organization_id: organizationId,
                        createdBy, // 👈 matches your schema
                        created_utc: new Date().toISOString(),
                        updated_utc: new Date().toISOString(),
                        is_published: false,
                        is_active: true,
                        external_id: crypto.randomUUID(),
                    },
                ])
                .select("id")
                .single();
            if (testError) throw testError;

            // 2. Insert bank
            const { data: bank, error: bankError } = await supabase
                .from("question_banks")
                .insert([
                    {
                        name: exam.Title,
                        organization_id: organizationId,
                    },
                ])
                .select("id")
                .single();
            if (bankError) throw bankError;

            // 3. Loop over questions
            for (let i = 0; i < exam.Questions.length; i++) {
                const q = exam.Questions[i];

                const { data: question, error: qError } = await supabase
                    .from("questions")
                    .insert([
                        {
                            test_id: test.id,             // 👈 add this line
                            text: q.QuestionText,
                            type: "multiple_choice",
                            order_num: i + 1,
                            is_active: true,
                            created_utc: new Date().toISOString(),
                            updated_utc: new Date().toISOString(),
                            external_id: crypto.randomUUID(),
                            bank_id: bank.id,             // ✅ link to question_banks
                            image_url: q.HelperFileName,  // optional helper image
                        },
                    ])
                    .select("id")
                    .single();

                if (qError) throw qError;

                // 4. Insert choices
                const options = [q.OptionA, q.OptionB, q.OptionC, q.OptionD];
                await supabase.from("choices").insert(
                    options.map((opt, idx) => ({
                        question_id: question.id,
                        text: opt,
                        is_correct: false,
                        order_num: idx + 1,
                    }))
                );

                // 5. Link to test
                await supabase.from("test_questions").insert([
                    {
                        test_id: test.id,
                        question_id: question.id,
                        original_order: i + 1,
                    },
                ]);
            }

            setMessage("✅ Exam imported successfully!");
            setExam(null);
        } catch (err: any) {
            console.error("Error importing exam:", err.message);
            setMessage("❌ Failed to import exam");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="bg-white p-4 rounded shadow">
            <h2 className="text-lg font-bold mb-4">Import Exam (JSON)</h2>

            <input type="file" accept=".json" onChange={handleFileUpload} className="mb-4" />

            {exam && (
                <div className="mb-4 p-3 border rounded bg-gray-50">
                    <p>
                        <strong>Title:</strong> {exam.Title}
                    </p>
                    <p>
                        <strong>Description:</strong> {exam.Description}
                    </p>
                    <p>
                        <strong>Duration:</strong> {exam.DurationMinutes} minutes
                    </p>
                    <p>
                        <strong>Questions:</strong> {exam.Questions.length}
                    </p>
                </div>
            )}

            {exam && (
                <button
                    onClick={handleImport}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded"
                >
                    {loading ? "Importing..." : "Import Exam"}
                </button>
            )}

            {message && <p className="mt-4">{message}</p>}
        </div>
    );
}
